import React, {useRef, useState} from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import HelpWidget from './../Help/HelpWidget';
import Button from '../../Components/Button/Button';
import Suggestion from './Suggestion';
import { Link } from 'react-router-dom';
import Icon from '../../Components/Icons/Icon';
import { useSession } from '../../Components/Session';
import { useDialogues } from '../../Components/Dialogues';
import { classNames, deviceIsMd, useNewWindow } from '../../Services/helpers';
import { usePreference } from '../../Data/Preference';
import AlertBadge from '../../Components/Icons/AlertBadge';
import { usePaymentsAccount } from '../../Data/Payments';
import { useWebsite } from '../../Data/Website';
import Loading from '../../Components/Loading';
import { Roles } from '../../Data/Users';

const More = () => {
    const [isSuggesting, setIsSuggesting] = useState(false);
    const scrollContainerRef = useRef();
    const {confirm} = useDialogues();
    const {logout} = useSession();
    const {account} = usePaymentsAccount();
    const newWindow = useNewWindow()

    return <>
        <TopBar title="Help & More"
            right={
                <Button theme="light" zeroMargin noShadow icon="Comment" onClick={() => setIsSuggesting(true)} />
            }
        />
        <Page scrollContainerRef={scrollContainerRef}>
            <HelpWidget />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-5">

                <Card title="My Business" icon="Building" color="bg-brand/20" scrollContainerRef={scrollContainerRef}>
                    <Menu items={[
                            {
                                to: '/billing',
                                title: 'Billing & Plan',
                                role: Roles.Owner
                            },
                            {
                                to: '/business/details',
                                title: 'Business Details',
                                role: Roles.Manager
                            },
                            {
                                to: '/business/services',
                                title: 'Services & Pricing',
                                role: Roles.Manager
                            },
                            {
                                to: '/business/documents',
                                title: 'Documents',
                                feature: 'DOCUMENTS',
                                role: Roles.Manager,
                            },
                            {
                                to: '/business/custom-sms-name',
                                title: 'Custom SMS name',
                                feature: 'SMS_CUSTOM_NAME',
                                role: Roles.Manager
                            },
                            {
                                to: '/business/additional-users',
                                title: 'Additional users',
                                feature: 'ADDITIONAL_USERS',
                                role: Roles.Owner
                            },
                            {
                                to: '/reports',
                                title: 'Reports',
                                role: Roles.Manager,
                            },
                    ]} />
                </Card>
                <Card title="Customers" icon="Users" color="bg-brand-light/30" scrollContainerRef={scrollContainerRef}>
                    <Menu items={[
                            {
                                to: '/settings/customer-notifications',
                                title: 'Notifications',
                                role: Roles.Manager
                            },
                            {
                                to: '/settings/message-templates',
                                title: 'Message templates',
                                role: Roles.Manager
                            },
                            {
                                to: '/settings/document-templates',
                                title: 'Document templates',
                                role: Roles.Manager
                            },
                            {
                                to: '/settings/quote-options',
                                title: 'Quote options',
                                role: Roles.Manager
                            },
                            {
                                to: '/settings/import',
                                title: 'Import customers',
                                role: Roles.Manager
                            },
                    ]} />
                </Card>
                <Card title="Payments" icon="CreditCardFront" color="bg-red-100" scrollContainerRef={scrollContainerRef}>
                    <Menu items={[
                            {
                                to: '/settings/payments/setup',
                                feature: 'CARDS_PAYMENTS',
                                title: 'Setup Payments',
                                hide: account?.setupProgress?.completed,
                                role: Roles.Owner,
                            },
                            {
                                to: '/settings/payments/bank-account',
                                title: 'Bank Account',
                                feature: 'CARDS_PAYMENTS',
                                disabled: !account?.setupProgress?.initialised, // Change these to completed
                                role: Roles.Owner
                            },
                            {
                                to: '/settings/payments/options',
                                title: 'Payment options',
                                feature: 'CARDS_PAYMENTS',
                                disabled: !account?.setupProgress?.completed,
                                role: Roles.Manager
                            },
                            {
                                to: '/settings/payments/payouts',
                                title: 'Payouts',
                                feature: 'CARDS_PAYMENTS',
                                disabled: !account?.setupProgress?.completed,
                                role: Roles.Owner
                            },
                            {
                                to: '/settings/payments/bank-reconciliation',
                                title: 'Bank Reconciliation',
                                feature: 'BANK_RECONCILIATION',
                                disabled: !account?.setupProgress?.initialised, // Change these to completed
                                role: Roles.Manager
                            },
                    ]} />
                </Card>
                <Card title="Website" icon="GlobePointer" color="bg-orange-100" scrollContainerRef={scrollContainerRef}>
                    <WebsiteMenu />
                </Card>
                <Card title="My Account" icon="User" color="bg-green-100" scrollContainerRef={scrollContainerRef}>
                    <Menu items={[
                            {
                                to: '/user/profile',
                                title: 'Profile & Password'
                            },
                            {
                                to: '/user/notification-preferences',
                                title: 'App Notifications'
                            },
                            {
                                to: '/verification',
                                title: 'Verification',
                                role: Roles.Owner
                            },
                            {
                                to: '/user/close-account',
                                title: 'Close account'
                            },
                            {
                                onClick: () => confirm(
                                    {theme: 'danger', title: 'Logout', message: 'Are you sure you want to logout?', confirmLabel: 'Logout'},
                                    async () => {
                                        await logout();
                                        window.location.reload();
                                    }
                                ),
                                title: 'Logout'
                            },
                    ]} />
                </Card>

                <Card title="getSoapy" icon="LOGO" color="bg-brand" scrollContainerRef={scrollContainerRef}>
                    <Menu items={[
                            {
                                to: '/get-social',
                                title: 'Follow us on social media'
                            },
                            {
                                onClick: () => newWindow('https://getsoapy.com/docs'),
                                title: 'Help using getSoapy'
                            },
                            {
                                to: '/whats-new',
                                title: 'What\'s new'
                            },
                            {
                                onClick: () => setIsSuggesting(true),
                                title: 'Feedback & suggestions'
                            },
                            {
                                to: '/more/communication-preferences',
                                title: 'Communication Preferences'
                            },
                            {
                                to: '/refer-a-friend',
                                title: 'Refer a friend'
                            },
                    ]} />
                </Card>
            </div>

        </Page>

        <Suggestion isOpen={isSuggesting} setIsOpen={setIsSuggesting} />
    </>
}

const WebsiteMenu = () => {
    const {website} = useWebsite(true);

    if (!website) {
        return <Loading center />;
    }

    if (!website.type) {
        return <Menu items={[
            {to: '/website/type', title: 'Setup your free website', role: Roles.Manager},
            {disabled: true, title: 'Change theme', role: Roles.Manager},
            {disabled: true, title: 'Update Content', role: Roles.Manager},
            {disabled: true, title: 'Custom domain', role: Roles.Manager},
            {disabled: true, title: 'Stats', role: Roles.Manager},
        ]} />
    }

    if (website.type === 'getsoapy') {
        return <Menu items={[
            {to: '/website/type', title: 'Change website type', role: Roles.Manager},
            {to: '/website/theme', title: 'Change theme', role: Roles.Manager},
            {to: '/website/content', title: 'Update Content', role: Roles.Manager},
            {to: '/website/custom-domain', title: 'Custom domain', feature: 'CUSTOM_DOMAIN', role: Roles.Manager},
            {to: '/website/links', title: 'Links', role: Roles.Manager},
            {to: '/website/stats', title: 'Stats', role: Roles.Manager},
        ]} />
    }

    if (website.type === 'thirdparty') {
        return <Menu items={[
            {to: '/website/type', title: 'Change website type', role: Roles.Manager},
            {to: '/website/third-party-settings', title: 'Domain & pages', role: Roles.Manager},
            {to: '/website/links', title: 'Links', role: Roles.Manager},
            {to: '/website/stats', title: 'Stats', role: Roles.Manager},
        ]} />
    }

    if (website.type === 'none') {
        return <Menu items={[
            {to: '/website/type', title: 'Change website type', role: Roles.Manager},
            {to: '/website/links', title: 'Links', role: Roles.Manager},
        ]} />
    }
}

const Card = ({scrollContainerRef, children, title, icon, color, className}) => {
    const collapsible = !deviceIsMd();
    const [expanded, setExpanded] = usePreference(`settings.${title}.expanded`, false, true);
    const ref = useRef();

    const isExpanded = collapsible === false || expanded === true;

    const toggle = () => {
        if (!collapsible) {
            return;
        }

        if (expanded === false) {
            setTimeout(() => {
                scrollContainerRef.current.scrollTo({
                    top: ref.current.offsetTop - 100,
                    behavior: 'smooth'
                });
            }, 100);
        }
        setExpanded(!expanded)
    }

    return <div ref={ref} className={classNames('transition-[padding] border rounded-xl px-4', isExpanded ? 'py-4' : ' py-2', className)}>
        <div className={classNames('flex justify-between items-center', isExpanded && 'mb-2', collapsible && 'cursor-pointer')}
            onClick={() => toggle()}
        >
            {title && <h3 className="grow font-bold -mt-1">{title}</h3>}
            {icon && <AlertBadge icon={icon} color={color} />}
        </div>

        {isExpanded ? children : null}
    </div>
}

const Menu = ({items}) => {
    const {hasFeature, isRoleSufficient} = useSession();
    const {featureUnavailable, roleInsufficient} = useDialogues();

    return <ul className="divide-y divide-gray-200">
        {items.map((item, i) => {
            if (item.hide) {
                return null;
            }

            const help = item.help ? <span className="text-xs text-muted"><br />{item.help}</span> : null;

            if ((item.feature && !hasFeature(item.feature)) || (item.role && !isRoleSufficient(item.role))) {
                return <li key={i}
                    className="py-2 text-muted flex flex-row justify-between"
                    onClick={() => (item.feature && !hasFeature(item.feature)) ? featureUnavailable(item.feature) : roleInsufficient(item.role)}
                >
                    {item.title}
                    {help}
                    <Icon icon="Lock" className="mr-2" inline />
                </li>
            }

            if (item.disabled) {
                return <li key={i} className="py-2 text-muted">
                    {item.title}
                    {help}
                </li>;
            }

            if (item.to) {
                return <li key={i}>
                    <Link className="inline-block w-full py-2" to={item.to}>
                        {item.title}
                        {help}
                    </Link>
                </li>
            }

            if (item.onClick) {
                return <li key={i} className="py-2 cursor-pointer" onClick={item.onClick}>
                    {item.title}
                    {help}
                </li>
            }

            return <li key={i} className="py-2">
                {item.title}
                {help}
            </li>;
        })}
    </ul>
}

export default More;
